import type { ValueOf } from 'type-fest';

import { Match } from 'effect';

import { Channel, getDefaultChannel } from '../config/channel';

export const UrlKey = {
  home: 'home',
  search: 'search',
  articles: 'articles',
  brands: 'brands',
  cart: 'cart',
  cartThankYou: 'cartThankYou',
  checkoutShippingAndPayment: 'checkoutShippingAndPayment',
  checkoutDeliveryDetails: 'checkoutDeliveryDetails',
  placeholder: 'placeholder',
  account: 'account',
  accountDashboard: 'accountDashboard',
  accountOrders: 'accountOrders',
  accountOrder: 'accountOrder',
  accountSettings: 'accountSettings',
  accountFavourites: 'accountFavourites',
  accountFavouritesList: 'accountFavouritesList',
  accountVerify: 'accountVerify',
  registration: 'registration',
  resetPassword: 'resetPassword',
  contact: 'contact',
  shopChrudim: 'shopChrudim',
  shopPardubice: 'shopPardubice',
  termsAndConditions: 'termsAndConditions',
  shipping: 'shipping',
  payment: 'payment',
  complaint: 'complaint',
  faq: 'faq',
  personalDataProcessing: 'personalDataProcessing',
  personalDataProcessingInformation: 'personalDataProcessingInformation',
  giftVouchers: 'giftVouchers',
  aboutUs: 'aboutUs',
  ourTeam: 'ourTeam',
  collaboration: 'collaboration',
  hiring: 'hiring',
  rcService: 'rcService',
  rcBazaar: 'rcBazaar',
  b2b: 'b2b',
  b2i: 'b2i',
  whyShopWithUs: 'whyShopWithUs',
  presentsTips: 'presentsTips',
  faqTopics: 'faqTopics',
  glossary: 'glossary',
  productsComparison: 'productsComparison',
} as const;

export type UrlKeyT = ValueOf<typeof UrlKey>;

export const Url = new Proxy(UrlKey, {
  get: (_, prop: string) => {
    return getUrl(prop as UrlKeyT);
  },
});

export type UrlT = ValueOf<typeof Url>;

// Channel is read lazily per call (stage-2 MR-A): the module-load
// `const DEFAULT_CHANNEL = getDefaultChannel()` env read made every importer
// of this route vocabulary read the tenant env at import time. The route
// vocabulary itself is tenant-package material (stage 3).
export const getUrl = (urlKey: UrlKeyT): string =>
  Match.value(getDefaultChannel()).pipe(
    Match.when(Channel.CZ_CHANNEL, () => UrlCz[urlKey]),
    Match.when(Channel.SK_CHANNEL, () => UrlSk[urlKey]),
    Match.exhaustive
  );

type UrlMap = Record<UrlKeyT, string>;

//////////////////////////////////////////////////////////////////
// urls for each country
// I did not move them to separate file, because I don't want those to be exported as they should not be used directly

const UrlCz: UrlMap = {
  home: '/',
  search: '/search',
  articles: '/poradna',
  brands: '/znacky',
  cart: '/cart',
  cartThankYou: '/cart/thank-you',
  checkoutShippingAndPayment: '/checkout/shipping-and-payment',
  checkoutDeliveryDetails: '/checkout/delivery-details',
  placeholder: '/',
  account: '/account',
  accountDashboard: '/account/dashboard',
  accountOrders: '/account/orders',
  accountOrder: '/account/orders/[id]',
  accountSettings: '/account/settings',
  registration: '/account/registration',
  resetPassword: '/account/reset-password',
  accountFavourites: '/account/favourites',
  accountFavouritesList: '/account/favourites/[id]',
  accountVerify: '/account/verify',
  contact: '/kontakt',
  shopChrudim: '/rc-prodejna-chrudim.html',
  shopPardubice: '/rc-prodejna-pardubice',
  termsAndConditions: '/obchodni-podminky',
  shipping: '/moznosti-dopravy.html',
  payment: '/moznosti-platby.html',
  complaint: '/jak-reklamovat.html',
  faq: '/i/faq',
  personalDataProcessing: '/zpracovani-osobnich-udaju.html',
  personalDataProcessingInformation: '/podminky-a-informace-o-zpracovani-osobnich-udaju.html',
  giftVouchers: '/darkove-poukazy.html',
  aboutUs: '/i/o-nas',
  ourTeam: '/i/tym',
  collaboration: '/i/spoluprace',
  hiring: '/volna-mista',
  rcService: '/rc-servis',
  rcBazaar: '/rc-bazar.html',
  b2b: '/pro-firmy',
  b2i: '/i/pro-skoly',
  whyShopWithUs: '/i/proc-nakupovat-u-nas',
  presentsTips: '/i/tipy-na-darky',
  faqTopics: '/faq',
  glossary: '/glosar',
  productsComparison: '/compare',
};

const UrlSk: UrlMap = {
  home: '/',
  search: '/search',
  articles: '/poradna',
  brands: '/znacky',
  cart: '/cart',
  cartThankYou: '/cart/thank-you',
  checkoutShippingAndPayment: '/checkout/shipping-and-payment',
  checkoutDeliveryDetails: '/checkout/delivery-details',
  placeholder: '/',
  account: '/account',
  accountDashboard: '/account/dashboard',
  accountOrders: '/account/orders',
  accountOrder: '/account/orders/[id]',
  accountSettings: '/account/settings',
  registration: '/account/registration',
  resetPassword: '/account/reset-password',
  accountFavourites: '/account/favourites',
  accountFavouritesList: '/account/favourites/[id]',
  accountVerify: '/account/verify',
  contact: '/kontakt',
  //should not be present on SK
  shopChrudim: '/',
  //should not be present on SK
  shopPardubice: '/',
  termsAndConditions: '/obchodne-podmienky.html',
  shipping: '/moznosti-dopravy.html',
  payment: '/moznosti-platby.html',
  complaint: '/ako-reklamovat.html',
  faq: '/i/faq',
  personalDataProcessing: '/spracovanie-osobnych-udajov.html',
  personalDataProcessingInformation: '/podmienky-a-informacie-o-spracovani-osobnych-udajov.html',
  giftVouchers: '/darcekove-poukazy',
  aboutUs: '/i/o-nas',
  ourTeam: '/i/tim',
  collaboration: '/i/spolupraca',
  hiring: '/i/volna-miesta',
  rcService: '/rc-servis',
  rcBazaar: '/rc-bazar.html',
  b2b: '/i/pre-firmy',
  b2i: '/i/pre-skoly',
  whyShopWithUs: '/i/preco-nakupovat-u-nas',
  presentsTips: '/i/tipy-na-darceky',
  faqTopics: '/faq',
  glossary: '/glosar',
  productsComparison: '/compare',
} as const;
